import React, { useEffect, useState } from 'react'
import { useDashboardContext } from '../../../../contexts/NewDashboardContext';
import { alertV2 } from '../../../../api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { Card, CardBody } from "../../../../components/card/card";
import { CardExpandToggler } from "../../../../components/card/card";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export const ManHour = ({}) => {
  const { dateRangeOverview } = useDashboardContext();
  const [chartData, setChartData] = useState(null);

  // Get theme colors from CSS variables
  const getThemeColors = () => {
    const bodyBg = getComputedStyle(document.body)
      .getPropertyValue("--bs-body-bg")
      .trim();
    const textColor = getComputedStyle(document.body)
      .getPropertyValue("--bs-body-color")
      .trim();
    const gridColor = getComputedStyle(document.body)
      .getPropertyValue("--bs-border-color")
      .trim();
    const themeColor = getComputedStyle(document.body)
      .getPropertyValue("--bs-theme")
      .trim();

    return { bodyBg, textColor, gridColor, themeColor };
  };

  const { bodyBg, textColor, gridColor, themeColor } = getThemeColors();

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1000,
      easing: "easeOutBounce",
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: "Man-Hour Saved",
        color: textColor,
        font: {
          size: 14,
          weight: "bold",
        },
      },

      tooltip: {
        backgroundColor: bodyBg,
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: gridColor,
        borderWidth: 1,
        callbacks: {
          label: function (context) {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${value.toLocaleString()} Hours`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: textColor,
          font: {
            size: 12,
          },
        },
        grid: {
          color: "#4b4b4b",
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Hour(s)",
          color: textColor,
          font: {
            size: 12,
          },
        },
        ticks: {
          color: textColor,
          font: {
            size: 12,
          },
          stepSize: 1,
        },
        grid: {
          color: "#4b4b4b",
          drawBorder: false,
        },
      },
    },
  };

  async function getData() {
    try {
      const result = await alertV2.listByDate({
        start_date: dateRangeOverview?.startDate,
        end_date: dateRangeOverview?.endDate,
        status: "Closed",
      });
      const body = await result.json();

      // Process the data to create chart data
      processChartData(body.data);
    } catch (error) {
      console.error("Error fetching alarm data:", error);
      // Use mock data for demonstration
      createMockChartData();
    }
  }

  const processChartData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) {
      console.log("No valid API data, using mock data");
      createMockChartData();
      return;
    }

    // Group maintenance costs by month based on max_date
    const monthlyMaintenanceCosts = {};

    apiData.forEach((alert) => {
      // Check if alert has maintenance cost data
      if (alert.max_date && alert.alertlogs && alert.alertlogs.length > 0) {
        const alertlog = alert.alertlogs[0];

        if (alertlog.action && alertlog.action.man_hour_saved) {
          const date = new Date(alert.max_date);
          const monthKey = `${date.getFullYear()}-${String(
            date.getMonth() + 1
          ).padStart(2, "0")}`;
          const monthName = date.toLocaleDateString("en-US", { month: "long" });

          if (!monthlyMaintenanceCosts[monthKey]) {
            monthlyMaintenanceCosts[monthKey] = {
              monthName: monthName,
              man_hour_saved: 0,
            };
          }

          // Add maintenance cost to the month total
          monthlyMaintenanceCosts[monthKey].man_hour_saved +=
            parseFloat(alertlog.action.man_hour_saved) || 0;
        }
      }
    });

    // Sort by date and get the data
    const sortedMonths = Object.keys(monthlyMaintenanceCosts).sort();

    // If no maintenance cost data, use mock data
    if (sortedMonths.length === 0) {
      console.log("No maintenance cost data found, using mock data");
      createMockChartData();
      return;
    }

    const labels = [];
    const data = [];

    sortedMonths.forEach((monthKey) => {
      const monthData = monthlyMaintenanceCosts[monthKey];
      labels.push(monthData.monthName);
      data.push(monthData.man_hour_saved);
    });

    const chartData = {
      labels: labels,
      datasets: [
        {
          label: `Man-Hour Saved`,
          data: data,
          borderColor: "#007bff",
          backgroundColor: "#007bff",
          borderWidth: 1,
          pointRadius: 4,
          pointHoverRadius: 8,
          tension: 0.1,
          fill: false,
        },
      ],
    };

    setChartData(chartData);
  };

  const createMockChartData = () => {
    const data = {
      labels: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      datasets: [
        {
          label: "Man-Hour Saved",
          data: [],
          borderColor: "#007bff",
          backgroundColor: "#007bff",
          borderWidth: 1,
          pointRadius: 4,
          pointHoverRadius: 8,
          tension: 0.1,
          fill: false,
        },
      ],
    };
    setChartData(data);
  };

  useEffect(() => {
    getData();
  }, [dateRangeOverview]);

  // If no data is available yet, show loading
  if (!chartData) {
    return (
      <div
        style={{
          height: "300px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: bodyBg,
          borderRadius: "8px",
          border: `1px solid ${gridColor}`,
        }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Card>
        <CardBody>
          <div className="d-flex justify-content-end">
            <CardExpandToggler />
          </div>
          <div style={{ position: "relative", height: "370px", width: "100%" }}>
            <Line options={options} data={chartData} />
          </div>
        </CardBody>
      </Card>
    </div>
  );
};
